import { ChangeEvent, FC, memo, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useAppDispatch, useAppSelector } from 'state/hooks'
import { selectAllTokens, selectInitialPrice, selectQuoteToken } from 'state/manage/selectors'
import NumericInput from 'components/Input/NumericInput'
import InputHeaderRow from 'components/Input/InputHeaderRow'
import InputTitle from 'components/Input/InputTitle'
import { setInitialPrice } from 'state/manage/reducer'
import Card, { CardBody } from 'components/Card'
import { FlexDivRowCentered } from 'components/layout/flex'
import { selectOraclePrices } from 'state/prices/selectors'
import { formatDollars } from 'utils/prices'

const InitialPriceInput: FC = memo(() => {
	const { t } = useTranslation()
	const dispatch = useAppDispatch()
	const initialPrice = useAppSelector(selectInitialPrice)
	const oraclePrices = useAppSelector(selectOraclePrices)
	const quoteToken = useAppSelector(selectQuoteToken)
	const allTokens = useAppSelector(selectAllTokens)

	const onChangeValue = (_: ChangeEvent<HTMLInputElement>, v: string) => {
		dispatch(setInitialPrice(v))
	}

	const price = useMemo(() => {
		if (quoteToken && oraclePrices && allTokens) {
			const targetToken = allTokens.find((token) => token.address === quoteToken)
			return (oraclePrices[targetToken?.symbol ?? ''] ?? 0) * Number(initialPrice)
		}
		return 0
	}, [quoteToken, oraclePrices, allTokens, initialPrice])

	const quoteTokenSymbol = useMemo(() => {
		if (quoteToken && allTokens) {
			const targetToken = allTokens.find((token) => token.address === quoteToken)
			return targetToken?.symbol || ''
		}
		return ''
	}, [quoteToken, allTokens])

	return (
		<CardContainer>
			<StyledCard data-testid="initial-price-input">
				<StyledCardBody>
					<PriceContainer>
						<InputHeaderRow
							label={
								<StyledInputTitle>
									{t('manage.list-pair.modal.initial-price')}
								</StyledInputTitle>
							}
						/>
						<InputContainer>
							<InputRow>
								<NumericInput
									dataTestId={'set-initial-price'}
									value={initialPrice}
									placeholder="0.00"
									right={false}
									onChange={onChangeValue}
									className="price-input"
								/>
								<StyledTokenSymbol>
									{quoteTokenSymbol}
								</StyledTokenSymbol>
							</InputRow>
							<StyledPriceText>
								{formatDollars(price, { suggestDecimals: true })}
							</StyledPriceText>
						</InputContainer>
					</PriceContainer>
				</StyledCardBody>
			</StyledCard>
		</CardContainer>
	)
})

const CardContainer = styled.div`
	display: grid;
	height: 183px;
`

const StyledCard = styled(Card)`
	background: ${(props) => props.theme.colors.selectedTheme.newTheme.exchange.card};
`

const StyledCardBody = styled(CardBody)`
	padding: 20px 32px;
`

const PriceContainer = styled(FlexDivRowCentered)`
	flex-direction: column;
	align-items: flex-start;
	width: 100%;
`

const StyledInputTitle = styled(InputTitle)`
	text-transform: capitalize;
	text-align: left;
	font-size: 14px;
	font-family: ${(props) => props.theme.fonts.regular};
	line-height: 1.25em;
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	width: 180px;
	padding-left: 20px;
`

const StyledPriceText = styled.p`
	color: ${(props) => props.theme.colors.selectedTheme.gray};
	margin: 0;
	padding: 0 0 0 5px;
	display: inline-block;
	font-size: 14px;
`

const StyledTokenSymbol = styled.span`
	color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	font-size: 18px;
	font-family: ${(props) => props.theme.fonts.regular};
	line-height: 1em;
	margin: 0 0 0 5px;
	display: inline-block;
	align-self: center;
`

const InputRow = styled(FlexDivRowCentered)`
	width: 100%;
	align-items: center;
	gap: 5px;
`

const InputContainer = styled(FlexDivRowCentered)`
	flex-direction: column;
	align-items: flex-start;
	width: 100%;
	max-width: 300px;
	gap: 8px;
	padding: 0px;
	border-radius: 10px;
	box-sizing: border-box;
	border: 1px solid ${(props) => props.theme.colors.selectedTheme.border};

	.price-input {
		width: 100%;
		
		& > div {
			height: 100%;
			border: none !important;
			padding: 0 !important;
			width: auto;
		}

		& input {
			height: 100%;
			padding: 0 5px !important;
			font-size: 18px;
			line-height: 1em;
			font-family: ${(props) => props.theme.fonts.regular};
			color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
		}
	}
`

export default InitialPriceInput
